import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { F8865SchdK1Service } from './f8865-schd-k1.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-f8865-schd-k1',
    templateUrl: './f8865-schd-k1.component.html',
    styleUrls: ['./f8865-schd-k1.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F8865SchdK1Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };

  // Modal properties
  title = 'Edit 8865 Schedule K1';
  minimise = false;
  isSaving = false;

  // Data from AngularJS
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};

  // Entity information
  tax_year: string = '';
  scenario: string = '';
  jcd_key: string = '';
  leid: string = '';
  le_name: string = '';
  combination_key: string = '';
  issue_key: string = '';
  sso_id: string = '';
  userType: number = 0;

  entityList: { [key: string]: string } = {};

  // Data arrays
  F8865SchdKWPDDetailForK1Popup: any[] = [];
  F8865SchdK1WPDDetailForEntity: any[] = [];
  K_K1_array: any[][] = [];

  // Loading states
  showFormLoader = false;

  // Initialization flag
  private isInitialized = false;
  private isLoading = false;
  /** Bumped on each load; stale HTTP callbacks no-op to avoid wrong grid state */
  private loadSeq = 0;
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  constructor(
    private f8865SchdK1Service: F8865SchdK1Service,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.loadSeq++;
    this.isInitialized = false;
    this.isLoading = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  /** Flat global params for legacy services that spread `globalService` into query/body. */
  private flatGlobalParams(): any {
    const gp = this.globalService?.globalParams;
    return extractObject(gp && Object.keys(gp).length ? gp : this.globalService);
  }

  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    
    // Check ALL required data before initializing
    const hasRowData = this.rowData && Object.keys(this.rowData).length > 0;
    const hasBaseUrl = this.GENERAL_CONFIG && (this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api_url);
    const hasScenario = this.filterParams && this.filterParams.scenario;
    const gp = this.flatGlobalParams();
    const hasJcdKey = gp && gp.jcd_key;
    
    
    // All conditions must be met
    if (hasRowData && hasBaseUrl && hasScenario && hasJcdKey) {
      this.isLoading = true;
      this.init();
    }
  }

  private getBaseUrl(): string {
    return this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api_url || '';
  }

  init() {
    try {
      // Set up entity information
      this.tax_year = this.rowData.TAX_YEAR;
      this.scenario = this.filterParams.scenario;
      this.jcd_key = this.flatGlobalParams().jcd_key;
      this.leid = this.rowData.LEID;
      this.le_name = this.rowData.LE_NAME;
      this.combination_key = this.rowData.COMBINATION_KEY;
      this.issue_key = this.rowData.ISSUE_KEY;
      this.sso_id = this.USER_SETTINGS?.user?.sso_id || '';
      this.userType = this.USER_SETTINGS?.user?.client?.user_type_key || 0;

      this.entityList = {
        'Tax Year': this.tax_year,
        'LEID': this.leid,
        'LE Name': this.le_name
      };

      // Load data (isInitialized set only after chained loads succeed)
      this.getF8865SchdKWPDDetailForK1Popup();
    } catch (e) {
      console.error('Error in F8865SchdK1 init:', e);
      this.isLoading = false;
      this.isInitialized = false;
    }
  }

  getF8865SchdKWPDDetailForK1Popup() {
    const seq = ++this.loadSeq;
    this.showFormLoader = true;

    const params = {
      tax_year: this.tax_year,
      scenario: this.scenario,
      jcd_key: this.jcd_key,
      combination_key: this.combination_key
    };


    this.f8865SchdK1Service.getF8865SchdKWPDDetailForK1Popup(
      this.getBaseUrl(),
      params,
      this.flatGlobalParams(),
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        if (response.callSuccess === '1') {
          this.F8865SchdKWPDDetailForK1Popup = response.jsonObject;
          
          // Load K1 details
          this.getF8865SchdK1WPDDetailForEntity(params, seq);
        } else {
          this.showFormLoader = false;
          this.isLoading = false;
          this.isInitialized = false;
          this.gtwSnackbarService.openSnackBar(
            response.errorMessage || 'Error loading Schedule K data',
            'gtw-snackbar--red'
          );
        }
      },
      (error: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.isInitialized = false;
        console.error('[K1] Error loading Schedule K data:', error);
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while loading data. Please try again.',
          'gtw-snackbar--red'
        );
      }
    );
  }

  getF8865SchdK1WPDDetailForEntity(params: any, parentSeq: number) {
    this.f8865SchdK1Service.getF8865SchdK1WPDDetailForEntity(
      this.getBaseUrl(),
      params,
      this.flatGlobalParams(),
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (parentSeq !== this.loadSeq) {
          return;
        }
        this.showFormLoader = false;
        this.isLoading = false;

        if (response.callSuccess === '1') {
          this.F8865SchdK1WPDDetailForEntity = response.jsonObject || [];
          
          // Combine K and K1 lines
          this.combineKAndK1Lines();
          this.isInitialized = true;
        } else {
          this.isInitialized = false;
          this.gtwSnackbarService.openSnackBar(
            response.errorMessage || 'Error loading Schedule K1 data',
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (parentSeq !== this.loadSeq) {
          return;
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.isInitialized = false;
        console.error('[K1] Error loading Schedule K1 data:', error);
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while loading data. Please try again.',
          'gtw-snackbar--red'
        );
      }
    );
  }

  combineKAndK1Lines() {
    if (!this.F8865SchdKWPDDetailForK1Popup || this.F8865SchdKWPDDetailForK1Popup.length === 0) {
      this.K_K1_array = [];
      return;
    }
    
    this.K_K1_array = new Array(this.F8865SchdKWPDDetailForK1Popup.length);

    for (let x = 0; x < this.F8865SchdKWPDDetailForK1Popup.length; x++) {
      const k_line = this.F8865SchdKWPDDetailForK1Popup[x];
      let K_and_K1_lines_combined: any[] = [];
      K_and_K1_lines_combined.push(k_line);

      // Filter K1 lines by LINE_ACCT_REFERENCE_KEY
      const k1_lines = this.F8865SchdK1WPDDetailForEntity.filter(
        (item: any) => item.LINE_ACCT_REFERENCE_KEY === k_line.LINE_ACCT_REFERENCE_KEY
      );

      let total = 0;
      for (let y = 0; y < k1_lines.length; y++) {
        K_and_K1_lines_combined.push(k1_lines[y]);
        total = total + (k1_lines[y].ADJ_AMT_1 || 0);
      }

      // Add TOTAL line
      const total_k_line = _.cloneDeep(k_line);
      total_k_line.LINE_NO = 'TOTAL_' + k_line.LINE_NO;
      total_k_line.ADJ_AMT_1 = total;
      K_and_K1_lines_combined.push(total_k_line);

      // Add VARIANCE line
      const variance_k_line = _.cloneDeep(k_line);
      variance_k_line.LINE_NO = 'VARIANCE_' + k_line.LINE_NO;
      variance_k_line.ADJ_AMT_1 = total - (k_line.ADJ_AMT_1 || 0);
      K_and_K1_lines_combined.push(variance_k_line);

      this.K_K1_array[x] = K_and_K1_lines_combined;
    }

  }

  setChange(row: any) {
    row.IS_CHANGED = 'Y';

    // Ensure ADJ_AMT_1 is a number
    if (row.ADJ_AMT_1 === undefined || row.ADJ_AMT_1 === null || row.ADJ_AMT_1 === '') {
      row.ADJ_AMT_1 = 0;
    } else {
      row.ADJ_AMT_1 = Number(row.ADJ_AMT_1) || 0;
    }

    // Update totals and variances
    for (let x = 0; x < this.K_K1_array.length; x++) {
      const tabular_K_K1_record = this.K_K1_array[x];
      
      // Get first row (partnership) to get the correct LINE_NO
      const firstRow = tabular_K_K1_record[0];
      if (!firstRow || firstRow.LINE_ACCT_REFERENCE_KEY !== row.LINE_ACCT_REFERENCE_KEY) {
        continue;
      }

      let total = 0;
      let pshipAmt = 0;

      for (let y = 0; y < tabular_K_K1_record.length; y++) {
        const item = tabular_K_K1_record[y];
        const itemAmount = Number(item.ADJ_AMT_1) || 0;
        
        // First row (y=0) is partnership
        if (y === 0) {
          pshipAmt = itemAmount;
        }
        // Entity rows (not partnership, not TOTAL, not VARIANCE)
        if (item.LEID !== this.leid && 
            !item.LINE_NO?.includes('TOTAL_') && 
            !item.LINE_NO?.includes('VARIANCE_')) {
          total = total + itemAmount;
        }
      }

      // Update TOTAL row
      const totalRow = tabular_K_K1_record.find(
        (item: any) => item.LINE_NO === 'TOTAL_' + firstRow.LINE_NO
      );
      if (totalRow) {
        totalRow.ADJ_AMT_1 = total;
      }

      // Update VARIANCE row
      const varianceRow = tabular_K_K1_record.find(
        (item: any) => item.LINE_NO === 'VARIANCE_' + firstRow.LINE_NO
      );
      if (varianceRow) {
        varianceRow.ADJ_AMT_1 = total - pshipAmt;
      }

      break;
    }
    
    // Trigger change detection
    this.cdr.detectChanges();
  }

  save() {
    const clobSettings = {
      tax_year: this.tax_year,
      scenario: this.scenario,
      jcd_key: this.jcd_key,
      combination_key: this.combination_key,
      sso_id: this.sso_id,
      issue_key: this.issue_key
    };

    const clobData: any[] = [];

    // Collect changed rows
    for (let x = 0; x < this.K_K1_array.length; x++) {
      const tabularK_K1 = this.K_K1_array[x];
      for (let y = 0; y < tabularK_K1.length; y++) {
        const row = tabularK_K1[y];
        if (row.IS_CHANGED === 'Y') {
          clobData.push(row);
        }
      }
    }

    if (clobData.length === 0) {
      this.gtwSnackbarService.openSnackBar('No changes to save.', 'gtw-snackbar--yellow');
      return;
    }

    // Extra validation: ensure required backend configuration is present
    const gp = this.flatGlobalParams();
    const hasScenarioCode =
      (gp && (gp.scenarioCode || gp.scenarioCode === 'RAF')) ||
      (this.filterParams && (this.filterParams.scenarioCode));
    const hasIssueKeyEnabled =
      (gp && (gp.is_issue_key_enabled !== undefined)) ||
      (this.filterParams && (this.filterParams.is_issue_key_enabled !== undefined));
    if (!hasScenarioCode || !hasIssueKeyEnabled) {
      this.gtwSnackbarService.openSnackBar(
        'Required configuration is missing: scenarioCode and is_issue_key_enabled.',
        'gtw-snackbar--red'
      );
      return;
    }

    // Validate - check for non-zero variance
    let validated = true;
    let validationMsg = '';

    for (let x = 0; x < this.K_K1_array.length; x++) {
      const tabularK_K1 = this.K_K1_array[x];
      for (let y = 0; y < tabularK_K1.length; y++) {
        const row = tabularK_K1[y];
        if (row.LINE_NO && row.LINE_NO.includes('VARIANCE')) {
          if (row.ADJ_AMT_1 !== 0) {
            validated = false;
            validationMsg = 'Variance exist for Line No : ' + tabularK_K1[0].LINE_NO + 
                           '. Please make sure no variance exist before saving';
            break;
          }
        }
      }
      if (!validated) {
        break;
      }
    }

    if (!validated) {
      this.gtwSnackbarService.openSnackBar(validationMsg, 'gtw-snackbar--red');
      return;
    }

    // Save
    this.isSaving = true;
    const message = '8865 Schedule K1 white paper details saved successfully.';

    this.f8865SchdK1Service.save8865SchdK1WPDForEntity(
      this.getBaseUrl(),
      clobSettings,
      clobData,
      this.flatGlobalParams(),
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaving = false;

        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaving = false;
        console.error('Error saving Schedule K1 data:', error);
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while saving. Please try again.',
          'gtw-snackbar--red'
        );
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Helper methods for template
  isFirstRow(index: number): boolean {
    return index === 0;
  }

  isEntityRow(row: any): boolean {
    return row.LEID !== this.leid && 
           !this.isTotalRow(row) && 
           !this.isVarianceRow(row);
  }

  isTotalRow(row: any): boolean {
    return row.LINE_NO && row.LINE_NO.includes('TOTAL_');
  }

  isVarianceRow(row: any): boolean {
    return row.LINE_NO && row.LINE_NO.includes('VARIANCE_');
  }

  isEditableRow(row: any): boolean {
    return this.isEntityRow(row) && 
           (row.LINE_NO !== '20A' && row.LINE_NO !== '20B');
  }

  isConditionalEditableRow(row: any): boolean {
    return this.isEntityRow(row) && 
           (row.LINE_NO === '20A' || row.LINE_NO === '20B') && 
           row.K1_EXISTS !== 0;
  }

  isDisabledRow(row: any): boolean {
    return this.isEntityRow(row) && 
           (row.LINE_NO === '20A' || row.LINE_NO === '20B') && 
           row.K1_EXISTS === 0;
  }

  canShowSaveButton(): boolean {
    return this.userType >= 2 && this.userType <= 5;
  }

  formatVariance(value: number): string {
    if (value < 0) {
      return '(' + Math.abs(value).toLocaleString() + ')';
    }
    return value.toLocaleString();
  }

  isNegativeVariance(value: number): boolean {
    return value < 0;
  }

  getEntityListKeys(): string[] {
    return Object.keys(this.entityList);
  }
}



